import { IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material"
import { PrintOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { Fragment } from "react"

import { InventoryLotListCell, InventoryProductDescriptionCell, InventoryProductIdCell } from "../../../../components"
import { formatInventoryLocation } from "../../../../utils"
import type { TransferPalletWithProduct } from "../../types"
import { calculateTransferTotals, formatPalletDestination } from "../../utils"

import type { TransferProduct } from "@/graphql/codegen/graphql"
import { formatNumber } from "@/utils/number"
import { isNil } from "@/utils/utils"

interface TransferPalletsTableProps {
  data: TransferProduct[]
  uomName: string | null
  isPrinting: boolean
  canPrintPalletLabel: boolean
  onPalletLabelPrint: (pallet: TransferPalletWithProduct) => void
}

const BASE_COLUMN_COUNT = 8
const PRINT_COLUMN_COUNT = 1

export const TransferPalletsTable: FC<TransferPalletsTableProps> = ({
  data,
  uomName,
  isPrinting,
  canPrintPalletLabel,
  onPalletLabelPrint,
}) => {
  const columnCount = canPrintPalletLabel ? BASE_COLUMN_COUNT + PRINT_COLUMN_COUNT : BASE_COLUMN_COUNT

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Product ID</TableCell>
            <TableCell>Description</TableCell>
            <TableCell>Reference #</TableCell>
            <TableCell>Current Location</TableCell>
            <TableCell>Lot</TableCell>
            <TableCell>Batch</TableCell>
            <TableCell>Qty to Move</TableCell>
            <TableCell>New Location</TableCell>
            {canPrintPalletLabel && <TableCell>Print QR Code</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((transferProduct) => {
            const totals = calculateTransferTotals([transferProduct])
            const pallets = transferProduct.pallets.length > 0 ? transferProduct.pallets : [undefined]

            return (
              <Fragment key={transferProduct.productId}>
                {pallets.map((pallet) => (
                  <TableRow
                    key={pallet?.referenceNbr ?? `product-${transferProduct.productId}`}
                    className='align-top'
                    data-testid={`transfer.pallets-table.row-${pallet?.referenceNbr ?? `product-${transferProduct.productId}`}`}
                  >
                    <TableCell>
                      <InventoryProductIdCell product={transferProduct.product} />
                    </TableCell>
                    <TableCell>
                      <InventoryProductDescriptionCell product={transferProduct.product} />
                    </TableCell>
                    <TableCell>{pallet?.referenceNbr ?? ""}</TableCell>
                    <TableCell>{formatInventoryLocation(pallet?.sourceLocation)}</TableCell>
                    <TableCell>
                      <InventoryLotListCell
                        lots={pallet?.lots}
                        getKey={(lot) => lot.transferLotId}
                        render={(lot) => lot.rawInventory?.lotNbr ?? ""}
                      />
                    </TableCell>
                    <TableCell>
                      <InventoryLotListCell
                        lots={pallet?.lots}
                        getKey={(lot) => lot.transferLotId}
                        render={(lot) => lot.rawInventory?.batchNbr ?? ""}
                      />
                    </TableCell>
                    <TableCell>
                      <InventoryLotListCell
                        lots={pallet?.lots}
                        getKey={(lot) => lot.transferLotId}
                        render={(lot) =>
                          isNil(lot.qtyToMove) ? "" : `${formatNumber(lot.qtyToMove)} ${uomName ?? ""}`
                        }
                      />
                    </TableCell>
                    <TableCell>{formatPalletDestination(pallet)}</TableCell>
                    {canPrintPalletLabel && (
                      <TableCell>
                        {pallet && (
                          <IconButton
                            size='small'
                            disabled={isPrinting}
                            onClick={() => onPalletLabelPrint({ ...pallet, product: transferProduct.product })}
                            data-testid='transfer.pallets-table.print-pallet-label-button'
                          >
                            <PrintOutlined />
                          </IconButton>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                <TableRow data-testid={`transfer.pallets-table.totals-row-${transferProduct.productId}`}>
                  <TableCell colSpan={columnCount}>
                    <div className='flex items-center justify-end gap-x-6 font-medium'>
                      <span>Total Pallets: {totals.palletCount}</span>
                      <span>
                        Total UoM: {formatNumber(totals.quantity)} {uomName ?? ""}
                      </span>
                    </div>
                  </TableCell>
                </TableRow>
              </Fragment>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
